// Fetches the client's public IP address (via ipify) for server-side audit
// logging. Caches successful lookups in localStorage so repeat calls on the
// same device don't need a fresh network call every time, and aren't exposed
// to a slow/hung ipify response. Only successful lookups are ever cached — a
// failed/timed-out lookup never overwrites a previously good value.
const CLIENT_IP_CACHE_KEY = 'clientPublicIp'
const CLIENT_IP_CACHE_FRESH_MS = 15 * 60 * 1000 // 15 minutes

const getCachedClientIp = () => {
  try {
    const raw = localStorage.getItem(CLIENT_IP_CACHE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

const setCachedClientIp = ip => {
  try {
    localStorage.setItem(
      CLIENT_IP_CACHE_KEY,
      JSON.stringify({ ip, timestamp: Date.now() })
    )
  } catch (error) {
    // ignore storage errors (quota, private mode, etc.)
  }
}

const fetchClientPublicIp = async () => {
  const cached = getCachedClientIp()
  const isFresh =
    cached && Date.now() - cached.timestamp < CLIENT_IP_CACHE_FRESH_MS

  if (isFresh) {
    return cached.ip
  }

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal
    })
    clearTimeout(timeoutId)
    if (!response.ok) throw new Error('ipify returned a non-OK response')
    const data = await response.json()
    if (!data || !data.ip) throw new Error('ipify response missing ip')
    setCachedClientIp(data.ip)
    return data.ip
  } catch (error) {
    // Live lookup failed/timed out — reuse a previous successful value
    // (even if stale) rather than losing the IP entirely, since the
    // device's real IP has almost certainly not changed since then.
    return cached ? cached.ip : undefined
  }
}

export default fetchClientPublicIp



// WEBPACK FOOTER //
// ./src/utils/fetchClientPublicIp.js